import { useState, useEffect } from 'react';

/**
 * Custom hook for managing localStorage state with automatic persistence
 * @param {string} key - localStorage key
 * @param {*} initialValue - Initial value if no stored value exists
 * @param {boolean} parseJSON - Whether to parse stored value as JSON (default: true)
 * @returns {Array} - [value, setValue, removeValue]
 */
export const useLocalStorage = (key, initialValue, parseJSON = true) => {
  // Get initial value from localStorage or use provided initial value
  const getStoredValue = () => {
    try {
      const item = localStorage.getItem(key);
      if (item === null) return initialValue;
      
      if (parseJSON) {
        return JSON.parse(item);
      }
      return item;
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  };

  const [storedValue, setStoredValue] = useState(getStoredValue);

  // Update localStorage when value changes
  useEffect(() => {
    try {
      if (storedValue === null || storedValue === undefined) {
        localStorage.removeItem(key);
      } else {
        const valueToStore = parseJSON ? JSON.stringify(storedValue) : storedValue;
        localStorage.setItem(key, valueToStore);
      }
    } catch (error) {
      console.warn(`Error setting localStorage key "${key}":`, error);
    }
  }, [key, storedValue, parseJSON]);

  // Function to remove the value from localStorage
  const removeValue = () => {
    try {
      localStorage.removeItem(key);
      setStoredValue(initialValue);
    } catch (error) {
      console.warn(`Error removing localStorage key "${key}":`, error);
    }
  };

  return [storedValue, setStoredValue, removeValue];
};

export default useLocalStorage;
